import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Drawer from "@/components/ui/drawer";
import Chip from "@/components/ui/chip";
import { Document } from "@/components/icons";
import { educationManagementApi } from "@/features/education-management";
import { EDUCATION_TYPE_API } from "@/features/education-management/types/education-management.types";
import type { EducationDetailApi } from "@/features/education-management";
import { virtualClassroomsApi } from "@/features/education-catalog/services/virtual-classrooms.api";
import type { VirtualClassroomResDto } from "@/features/education-catalog/services/virtual-classrooms.api";
import styles from "./ApprovalDetailDrawer.module.scss";

interface ApprovalDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  educationId: string | null;
  educationName?: string;
}

const formatDate = (iso?: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const ApprovalDetailDrawer: React.FC<ApprovalDetailDrawerProps> = ({
  isOpen,
  onClose,
  educationId,
  educationName,
}) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<EducationDetailApi | null>(null);
  const [classrooms, setClassrooms] = useState<VirtualClassroomResDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !educationId) return;
    let cancelled = false;
    setIsLoading(true);
    Promise.all([
      educationManagementApi.getById(educationId).then(({ data }) => data),
      virtualClassroomsApi.listByEducation(educationId).catch(() => []),
    ])
      .then(([d, vcs]) => {
        if (cancelled) return;
        setDetail(d);
        setClassrooms(vcs);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error(
          t("approvalDetail.loadError"),
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, educationId, t]);

  // Backend 22.04.2026'dan itibaren enrollmentBreakdown dönüyor; eski davranışa
  // fallback olması için `requestedEnrollmentsCount` / `totalEnrolledCount`
  // alanlarını hâlâ kullanabiliyoruz.
  const breakdown = detail?.enrollmentBreakdown;
  const pendingCount =
    breakdown?.pending ?? detail?.requestedEnrollmentsCount ?? 0;
  const enrolledCount = breakdown?.enrolled ?? detail?.totalEnrolledCount ?? 0;
  const rejectedCount = breakdown?.rejected ?? 0;
  const cancelledCount = breakdown?.cancelled ?? 0;
  const totalApplications =
    breakdown?.total ??
    pendingCount + enrolledCount + rejectedCount + cancelledCount;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={t("approvalDetail.title")}
      headerIcon={<Document width={20} height={20} />}
      width="480px"
    >
      <div className={styles.content}>
        {isLoading || !detail ? (
          <div className={styles.empty}>
            {t("common.loading")}
          </div>
        ) : (
          <>
            <section className={styles.section}>
              <div className={styles.sectionTitle}>
                {t("approvalDetail.educationInfo")}
              </div>
              <dl className={styles.kv}>
                <div>
                  <dt>{t("approvalDetail.name")}</dt>
                  <dd>{detail.name || educationName || "—"}</dd>
                </div>
                <div>
                  <dt>{t("approvalDetail.code")}</dt>
                  <dd>{detail.code ?? "—"}</dd>
                </div>
                <div>
                  <dt>{t("approvalDetail.startDate")}</dt>
                  <dd>{formatDate(detail.startDate)}</dd>
                </div>
                <div>
                  <dt>{t("approvalDetail.endDate")}</dt>
                  <dd>{formatDate(detail.endDate)}</dd>
                </div>
              </dl>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionTitle}>
                {t("approvalDetail.stats")}
              </div>
              <div className={styles.stats}>
                <div className={styles.statCard}>
                  <span className={styles.statValue}>{totalApplications}</span>
                  <span className={styles.statLabel}>
                    {t("approvalDetail.totalRequests")}
                  </span>
                </div>
                <div className={styles.statCard}>
                  <span className={`${styles.statValue} ${styles.warning}`}>
                    {pendingCount}
                  </span>
                  <span className={styles.statLabel}>
                    {t("approvalDetail.pendingParticipants")}
                  </span>
                </div>
                <div className={styles.statCard}>
                  <span className={`${styles.statValue} ${styles.success}`}>
                    {enrolledCount}
                  </span>
                  <span className={styles.statLabel}>
                    {t("approvalDetail.approvedParticipants")}
                  </span>
                </div>
                <div className={styles.statCard}>
                  <span className={`${styles.statValue} ${styles.danger}`}>
                    {rejectedCount}
                  </span>
                  <span className={styles.statLabel}>
                    {t("approvalDetail.rejectedParticipants")}
                  </span>
                </div>
                <div className={styles.statCard}>
                  <span className={`${styles.statValue} ${styles.muted}`}>
                    {cancelledCount}
                  </span>
                  <span className={styles.statLabel}>
                    {t("approvalDetail.cancelledParticipants")}
                  </span>
                </div>
              </div>
            </section>

            {(detail.type === EDUCATION_TYPE_API.ON_SITE ||
              detail.type === EDUCATION_TYPE_API.ONLINE) && (
            <section className={styles.section}>
              <div className={styles.sectionTitle}>
                {t("approvalDetail.classrooms")}
                <Chip size="small" type="info" variant="outlined">
                  {classrooms.length}
                </Chip>
              </div>
              {classrooms.length === 0 ? (
                <div className={styles.empty}>
                  {t("approvalDetail.noClassrooms")}
                </div>
              ) : (
                <ul className={styles.classList}>
                  {classrooms.map((vc) => (
                    <li key={vc.id} className={styles.classItem}>
                      <div className={styles.classHeader}>
                        <span className={styles.className}>
                          {vc.name?.trim() || "—"}
                        </span>
                        {vc.jitsiRoomName && (
                          <Chip size="small" type="default" variant="outlined">
                            {vc.jitsiRoomName}
                          </Chip>
                        )}
                      </div>
                      <div className={styles.classMeta}>
                        <span>
                          {formatDate(vc.startDate)} —{" "}
                          {formatDate(vc.endDate)}
                        </span>
                        {vc.location && (
                          <span className={styles.classLoc}>{vc.location}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default ApprovalDetailDrawer;
